import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ScanLine, Camera, Package, Box, TrendingUp, TrendingDown, 
  Check, X, ArrowRight, QrCode, Keyboard, CameraOff, Video
} from 'lucide-react';
import { useQrScanner } from '../hooks/useQrScanner';
import { ArticleAutocomplete } from '../components/ui/article-autocomplete';

const ScannerPage = () => {
  const { plants } = useData();
  const [mode, setMode] = useState('receive'); // receive, issue, transfer
  const [scanInput, setScanInput] = useState('');
  const [scannedArticle, setScannedArticle] = useState(null);
  const [bins, setBins] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const inputRef = useRef(null);
  const scannerRef = useRef(null);
  const { start: startQr, stop: stopQr } = useQrScanner();

  // Form states
  const [selectedBin, setSelectedBin] = useState('');
  const [targetBin, setTargetBin] = useState('');
  const [quantity, setQuantity] = useState('');
  const [lotNumber, setLotNumber] = useState('');

  useEffect(() => {
    fetchBins();
    fetchArticles();
    return () => { stopQr(); };
  }, []);

  const fetchBins = async () => {
    try {
      const response = await api.get('/bins');
      setBins(response.data || []);
    } catch (error) {
      console.error('Error fetching bins:', error);
    }
  };

  const fetchArticles = async () => {
    try {
      const response = await api.get('/articles');
      setArticles(response.data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const startCamera = async () => {
    setCameraError(null);
    setCameraActive(true);
    try {
      await new Promise(r => setTimeout(r, 300));
      await startQr('qr-reader', (decodedText) => {
        handleScan(decodedText);
        toast.success(`Cod scanat: ${decodedText}`);
      }, { fps: 10, qrbox: { width: 250, height: 250 } });
      toast.success('Camera activata cu succes!');
    } catch (err) {
      console.error('Camera error:', err);
      let errorMsg = err.message || 'Nu se poate accesa camera';
      if (errorMsg.includes('Permission denied') || errorMsg.includes('NotAllowedError')) {
        errorMsg = 'Acces interzis la camera. Permiteti accesul in setarile browserului.';
      } else if (errorMsg.includes('NotFoundError')) {
        errorMsg = 'Nu s-a gasit nicio camera pe acest dispozitiv.';
      } else if (errorMsg.includes('NotReadableError')) {
        errorMsg = 'Camera este folosita de alta aplicatie.';
      }
      setCameraError(errorMsg);
      setCameraActive(false);
      toast.error(errorMsg);
    }
  };

  const stopCamera = async () => {
    await stopQr();
    setCameraActive(false);
  };

  const toggleCamera = () => {
    if (cameraActive) stopCamera();
    else startCamera();
  };

  const handleScan = async (code) => {
    if (!code.trim()) return;
    
    setLoading(true);
    try {
      // Parse QR format TYPE:VALUE (e.g., ART:ART-000019)
      let searchTerm = code;
      const parts = code.split(':');
      if (parts.length >= 2 && parts[0] === 'ART') {
        searchTerm = parts.slice(1).join(':'); // ART:ART-000019 -> ART-000019
      }
      
      const response = await api.get('/articles', { params: { search: searchTerm } });
      const articles = response.data || [];
      
      if (articles.length === 0) {
        toast.error(`Articol negasit: ${searchTerm}`);
        setScannedArticle(null);
      } else {
        setScannedArticle(articles[0]);
        toast.success(`Articol gasit: ${articles[0].name}`);
        if (cameraActive) stopCamera();
      }
    } catch (error) {
      toast.error('Eroare la cautare articol');
    } finally {
      setLoading(false);
      setScanInput('');
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleScan(scanInput);
    }
  };

  const handleAction = async () => {
    if (!scannedArticle || !quantity || parseFloat(quantity) <= 0) {
      toast.error('Completati toate campurile');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'receive') {
        if (!selectedBin) {
          toast.error('Selectati locatia BIN');
          setLoading(false);
          return;
        }
        await api.post('/stock/receive', {
          article_id: scannedArticle.id,
          bin_id: selectedBin,
          quantity: parseFloat(quantity),
          lot_number: lotNumber,
          notes: `Receptie rapida via scanner`
        });
        toast.success('Receptie inregistrata!');
      } else if (mode === 'issue') {
        if (!selectedBin) {
          toast.error('Selectati locatia BIN');
          setLoading(false);
          return;
        }
        await api.post('/stock/issue', {
          article_id: scannedArticle.id,
          bin_id: selectedBin,
          quantity: parseFloat(quantity),
          notes: `Eliberare rapida via scanner`
        });
        toast.success('Eliberare inregistrata!');
      } else if (mode === 'transfer') {
        if (!selectedBin || !targetBin) {
          toast.error('Selectati ambele locatii');
          setLoading(false);
          return;
        }
        await api.post('/stock/transfer', {
          article_id: scannedArticle.id,
          from_bin_id: selectedBin,
          to_bin_id: targetBin,
          quantity: parseFloat(quantity),
          notes: `Transfer rapid via scanner`
        });
        toast.success('Transfer efectuat!');
      }

      // Reset form
      setScannedArticle(null);
      setSelectedBin('');
      setTargetBin('');
      setQuantity('');
      setLotNumber('');
      
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la procesare');
    } finally {
      setLoading(false);
    }
  };

  const getModeConfig = () => {
    switch (mode) {
      case 'receive':
        return {
          title: 'Receptie Stoc',
          icon: TrendingUp,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200'
        };
      case 'issue':
        return {
          title: 'Eliberare Stoc',
          icon: TrendingDown,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200'
        };
      case 'transfer':
        return {
          title: 'Transfer Stoc',
          icon: ArrowRight,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200'
        };
      default:
        return {
          title: 'Scanner',
          icon: ScanLine,
          color: 'text-slate-600',
          bgColor: 'bg-slate-50',
          borderColor: 'border-slate-200'
        };
    }
  };

  const config = getModeConfig();
  const IconComponent = config.icon;

  return (
    <div className="max-w-2xl mx-auto space-y-6 pb-20" data-testid="scanner-page">
      {/* Mode Selection */}
      <div className="flex flex-col items-center gap-4">
        <h1 className="text-2xl font-bold text-slate-900">Scanner Rapid WMS</h1>
        <div className="flex gap-2 flex-wrap justify-center">
          <Button 
            variant={mode === 'receive' ? 'default' : 'outline'}
            onClick={() => setMode('receive')}
            className={mode === 'receive' ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Receptie
          </Button>
          <Button 
            variant={mode === 'issue' ? 'default' : 'outline'}
            onClick={() => setMode('issue')}
            className={mode === 'issue' ? 'bg-red-600 hover:bg-red-700' : ''}
          >
            <TrendingDown className="w-4 h-4 mr-2" />
            Eliberare
          </Button>
          <Button 
            variant={mode === 'transfer' ? 'default' : 'outline'}
            onClick={() => setMode('transfer')}
            className={mode === 'transfer' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          >
            <ArrowRight className="w-4 h-4 mr-2" />
            Transfer
          </Button>
        </div>
      </div>

      {/* Camera Scanner */}
      <Card className={`${config.bgColor} ${config.borderColor} border-2`}>
        <CardHeader className="pb-2">
          <CardTitle className={`flex items-center gap-2 ${config.color}`}>
            <IconComponent className="w-5 h-5" />
            {config.title}
          </CardTitle>
          <CardDescription>Scanati cu camera sau introduceti manual codul</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Camera Toggle Button */}
          <Button 
            onClick={toggleCamera}
            variant={cameraActive ? "destructive" : "default"}
            className={`w-full h-14 text-lg ${!cameraActive ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
          >
            {cameraActive ? (
              <>
                <CameraOff className="w-5 h-5 mr-2" />
                Opreste Camera
              </>
            ) : (
              <>
                <Camera className="w-5 h-5 mr-2" />
                Activeaza Camera pentru Scanare
              </>
            )}
          </Button>

          {/* Camera View - Always in DOM, hidden when not active */}
          <div className={`relative ${cameraActive ? 'block' : 'hidden'}`}>
            <div 
              id="qr-reader" 
              ref={scannerRef}
              style={{ 
                width: '100%', 
                minHeight: '350px',
                maxHeight: '450px'
              }}
            />
            {cameraActive && !cameraError && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-64 h-64 border-2 border-green-400/70 rounded-lg relative">
                  <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-green-400 rounded-tl-lg" />
                  <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-green-400 rounded-tr-lg" />
                  <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-green-400 rounded-bl-lg" />
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-green-400 rounded-br-lg" />
                </div>
              </div>
            )}
          </div>

          {cameraError && (
            <div className="p-3 bg-red-100 border border-red-200 rounded-lg text-red-700 text-sm">
              <p className="font-medium">Eroare camera:</p>
              <p>{cameraError}</p>
              <p className="mt-2 text-xs">Asigurati-va ca ati acordat permisiunea pentru camera in browser.</p>
            </div>
          )}

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className={`${config.bgColor} px-2 text-slate-500`}>sau cautati articol</span>
            </div>
          </div>

          {/* Article Search - ArticleAutocomplete */}
          <div className="space-y-2" data-testid="article-autocomplete-scanner">
            <ArticleAutocomplete
              articles={articles}
              onSelect={(article) => {
                if (article) {
                  setScannedArticle(article);
                  toast.success(`Articol selectat: ${article.name}`);
                }
              }}
              placeholder="Cautati articol dupa nume sau cod..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Scanned Article Display */}
      {scannedArticle && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-600" />
                Articol Scanat
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setScannedArticle(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h3 className="font-semibold text-lg">{scannedArticle.name}</h3>
              <p className="text-slate-500">Cod: {scannedArticle.code}</p>
              <div className="flex items-center gap-4 mt-2 flex-wrap">
                <Badge variant="outline">
                  Stoc total: {scannedArticle.total_stock || 0} {scannedArticle.unit}
                </Badge>
                {scannedArticle.min_stock > 0 && (
                  <Badge variant={scannedArticle.total_stock <= scannedArticle.min_stock ? 'destructive' : 'outline'}>
                    Min: {scannedArticle.min_stock} {scannedArticle.unit}
                  </Badge>
                )}
              </div>
            </div>

            {/* Action Form */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{mode === 'transfer' ? 'Din BIN' : 'Locatie BIN'}</Label>
                  <Select value={selectedBin} onValueChange={setSelectedBin}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selectati" />
                    </SelectTrigger>
                    <SelectContent>
                      {bins.map(bin => (
                        <SelectItem key={bin.id} value={bin.id}>
                          {bin.code} - {bin.description || 'N/A'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {mode === 'transfer' && (
                  <div className="space-y-2">
                    <Label>In BIN</Label>
                    <Select value={targetBin} onValueChange={setTargetBin}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selectati" />
                      </SelectTrigger>
                      <SelectContent>
                        {bins.filter(b => b.id !== selectedBin).map(bin => (
                          <SelectItem key={bin.id} value={bin.id}>
                            {bin.code} - {bin.description || 'N/A'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Cantitate *</Label>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder={`0 ${scannedArticle.unit}`}
                    className="h-12"
                  />
                </div>

                {mode === 'receive' && (
                  <div className="space-y-2">
                    <Label>Nr. Lot (optional)</Label>
                    <Input
                      value={lotNumber}
                      onChange={(e) => setLotNumber(e.target.value)}
                      placeholder="LOT-XXX"
                      className="h-12"
                    />
                  </div>
                )}
              </div>

              <Button 
                onClick={handleAction}
                disabled={loading || !quantity || parseFloat(quantity) <= 0 || !selectedBin}
                className={`w-full h-14 text-lg ${
                  mode === 'receive' ? 'bg-green-600 hover:bg-green-700' :
                  mode === 'issue' ? 'bg-red-600 hover:bg-red-700' :
                  'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                <Check className="w-5 h-5 mr-2" />
                {loading ? 'Se proceseaza...' : 
                 mode === 'receive' ? 'Confirma Receptie' :
                 mode === 'issue' ? 'Confirma Eliberare' :
                 'Confirma Transfer'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Instructions */}
      <Card className="bg-slate-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <QrCode className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
            <div className="text-sm text-slate-600">
              <p className="font-medium mb-1">Instructiuni:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Selectati tipul operatiei (Receptie/Eliberare/Transfer)</li>
                <li>Apasati <strong>&quot;Activeaza Camera&quot;</strong> si indreptati spre cod</li>
                <li>Sau cautati si selectati articolul din lista</li>
                <li>Completati cantitatea si locatia BIN</li>
                <li>Confirmati operatia</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScannerPage;
